<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div
    class="grid-container"
    :style="{
      gridTemplateColumns: gridSettings?.columns
        ? `repeat(${gridSettings.columns}, 1fr)`
        : 'repeat(1, 1fr)',
      gridTemplateRows: `repeat(${gridSettings.rows}, 100px)`,
    }"
    v-if="configJson"
  >
    <div
      v-for="element in validatedBlocConfig"
      :key="element.id"
      class="card"
      :style="{
        gridColumn: `${element.gridPosition.colStart} / ${element.gridPosition.colEnd}`,
        gridRow: `${element.gridPosition.rowStart} / ${element.gridPosition.rowEnd}`,
      }"
    >
      <div class="block-header">
        <h4 v-if="element.title" class="block-title">{{ element.title }}</h4>

        <RouterLink
          v-if="element.route && element.route.name"
          :to="{ name: element.route.name }"
          class="block-link"
        >
          <i class="ri-fullscreen-line"></i>
        </RouterLink>
      </div>

      <div class="block-content">
        <component
          :is="getBlockComponent(element.type)"
          v-bind="getBlockProps(element)"
        />
      </div>
    </div>

    <!-- overlay grid lines -->
    <div
      v-if="showOverlay"
      class="grid-overlay"
      :style="{
        gridTemplateColumns: `repeat(${gridSettings.columns}, 1fr)`,
        gridTemplateRows: `repeat(${gridSettings.rows}, 1fr)`,
      }"
    >
      <div
        v-for="n in gridSettings.columns * gridSettings.rows"
        :key="n"
        class="grid-cell"
      />
    </div>
  </div>
</template>
<script>
import { defineAsyncComponent } from "vue";

export default {
  name: "DashboardComponent",
  components: {
    FavoriteList: defineAsyncComponent(
      () => import("@/components/dashboard/widgets/FavoriteList.vue"),
    ),
    ActivityHistory: defineAsyncComponent(
      () => import("@/components/dashboard/widgets/ActivityHistory.vue"),
    ),
    ExternalApps: defineAsyncComponent(
      () => import("@/components/dashboard/widgets/ExternalApps.vue"),
    ),
    MarkdownComponent: defineAsyncComponent(
      () => import("@/components/dashboard/widgets/MarkdownComponent.vue"),
    ),
    BookmarksComponent: defineAsyncComponent(
      () => import("@/components/dashboard/widgets/BookmarksComponent.vue"),
    ),
    SearchInput: defineAsyncComponent(
      () => import("@/components/dashboard/widgets/SearchInput.vue"),
    ),
    DisplayFileInDashboardComponent: defineAsyncComponent(
      () =>
        import("@/components/dashboard/widgets/DisplayFileInDashboardComponent.vue"),
    ),
    ChartWrapper: defineAsyncComponent(
      () => import("@/components/dashboard/widgets/ChartWrapper.vue"),
    ),
    UnknownBlockType: defineAsyncComponent(
      () => import("@/components/dashboard/widgets/UnknownBlockType.vue"),
    ),
  },
  props: {
    availableConfigs: {
      type: Array,
      default: () => [],
    },
    customWidgets: {
      type: Object,
      default: () => ({}),
    },
    urlResolver: {
      type: Function,
      default: null,
    },
    searchResolver: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      selectedConfigKey: null,
      showOverlay: false,
      configJson: null,
    };
  },
  mounted() {
    if (!this.selectedConfigKey && this.availableConfigs.length > 0) {
      this.selectedConfigKey = this.availableConfigs[0].key;
    }
    this.loadConfig();
  },
  computed: {
    gridSettings() {
      return this.configJson?.gridSettings || { columns: 3, rows: 4 };
    },
    validatedBlocConfig() {
      if (!this.configJson?.blocConfig) {
        return [];
      }

      return this.configJson.blocConfig.filter((block) => {
        const isValid = this.validateBlock(block);
        if (!isValid) {
          console.warn(
            `Block "${block.id}" has invalid grid position:`,
            block.gridPosition,
          );
        }
        return isValid;
      });
    },
  },
  methods: {
    getBlockComponent(type) {
      // Custom widgets take priority (allows app-specific overrides)
      if (this.customWidgets[type]) {
        return this.customWidgets[type];
      }

      const componentMap = {
        markdown: "MarkdownComponent",
        favorites: "FavoriteList",
        favoriteFile: "FavoriteList",
        favoriteSite: "FavoriteList",
        chart: "ChartWrapper",
        myActivity: "ActivityHistory",
        pdf: "DisplayFileInDashboardComponent",
        image: "DisplayFileInDashboardComponent",
        bookmarks: "BookmarksComponent",
        search: "SearchInput",
        externalApps: "ExternalApps",
      };

      return componentMap[type] || "UnknownBlockType";
    },

    getBlockProps(element) {
      const propsMap = {
        markdown: {
          id: element.config?.id,
          markdownContent: element.config?.markdown,
        },
        favorites: {
          filter: element.config?.filter || "file",
          siteId: element.config?.siteId || "",
          urlResolver: this.urlResolver,
        },
        favoriteFile: {
          filter: "file",
          urlResolver: this.urlResolver,
        },
        favoriteSite: {
          filter: "site",
          urlResolver: this.urlResolver,
        },
        myActivity: {
          urlResolver: this.urlResolver,
        },
        chart: {
          type: element.config?.type,
          data: element.config?.data,
          options: element.config?.options,
        },
        pdf: {
          id: element.config?.id,
          type: "pdf",
        },
        image: {
          id: element.config?.id,
          type: "image",
        },
        bookmarks: {
          bookmarks: element.config?.bookmarks,
          urlResolver: this.urlResolver,
        },
        search: {
          searchResolver: this.searchResolver,
        },
        externalApps: {
          config: element.config?.links,
          useFavicon: element.config?.useFavicon,
        },
      };

      // Custom widgets receive the full element
      if (this.customWidgets[element.type]) {
        return { element };
      }

      // For unknown types, pass the type
      if (!propsMap[element.type]) {
        return { type: element.type };
      }

      return propsMap[element.type] || {};
    },

    validateBlock(block) {
      if (!block.gridPosition) {
        return false;
      }

      const { colStart, colEnd, rowStart, rowEnd } = block.gridPosition;
      const { columns, rows } = this.gridSettings;

      // Check that all positions are defined and are numbers
      if (
        typeof colStart !== "number" ||
        typeof colEnd !== "number" ||
        typeof rowStart !== "number" ||
        typeof rowEnd !== "number"
      ) {
        return false;
      }

      // Check that start < end
      if (colStart >= colEnd || rowStart >= rowEnd) {
        return false;
      }

      // Check that positions are within grid bounds
      if (colEnd > columns + 1 || rowEnd > rows + 1) {
        return false;
      }

      // Check that start positions are positive
      if (colStart < 1 || rowStart < 1) {
        return false;
      }

      return true;
    },
    loadConfig() {
      if (!this.selectedConfigKey) {
        return;
      }
      const config = this.availableConfigs.find(
        (c) => c.key === this.selectedConfigKey,
      );
      this.configJson = config.configJson;
    },
    exportDashBoard() {
      console.log(JSON.stringify(this.availableConfigs));
    },
  },
};
</script>

<style scoped>
.grid-container {
  display: grid;
  gap: 1rem;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  pointer-events: none;
  z-index: 10;
}

.grid-cell {
  border: 1px solid #ddd;
  box-sizing: border-box;
}

.card {
  position: relative;
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  padding: 1.5rem;
  border-radius: 1rem;
  width: 100%;
  height: 99%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-sizing: border-box;
  justify-self: center;
}

.block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.block-title {
  margin: 0;
}

.block-link {
  font-size: 1.25rem;
  cursor: pointer;
}

.block-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.card-content * {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

@media (max-width: 768px) {
  .grid-container {
    display: flex;
    flex-direction: column;
  }

  .card {
    width: 100% !important;
    height: auto !important;
    margin-bottom: 1rem;
  }

  .grid-overlay {
    display: none !important;
  }
}
</style>
